/**
 * EnhancedSecureTokenManager - Advanced token management for Google Contacts
 * 
 * Key Features:
 * - Encrypted token storage using Web Crypto API
 * - Proactive token refresh with 10-minute threshold
 * - Exponential backoff retry logic
 * - CSP-compliant implementation
 * - Background token monitoring
 * - Secure session management
 */

import cryptoUtils from '../utils/cryptoUtils.js';

class EnhancedSecureTokenManager {
  constructor() {
    this.TOKEN_KEY = 'secure_contacts_tokens';
    this.ENCRYPTION_KEY = 'contacts_encryption_key';
    this.TOKEN_EXPIRY = 50 * 60 * 1000; // 50 minutes (shorter than Google's 60min)
    this.REFRESH_THRESHOLD = 10 * 60 * 1000; // Refresh when 10 minutes left
    this.PROACTIVE_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes extra buffer
    
    // Retry configuration with exponential backoff
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2
    };
    
    // Token refresh management
    this.refreshTimeouts = new Map();
    this.refreshPromises = new Map();
    this.refreshCallbacks = new Set();
    
    // Performance metrics
    this.metrics = {
      refreshAttempts: 0,
      refreshSuccesses: 0,
      refreshFailures: 0,
      totalRefreshTime: 0,
      averageRefreshTime: 0
    };
    
    this.initializeEncryption();
    this.setupBackgroundMonitoring();
  }

  /**
   * Initialize encryption system
   */
  async initializeEncryption() {
    try {
      // Generate or retrieve encryption key
      let encryptionKeyData = sessionStorage.getItem(this.ENCRYPTION_KEY);
      
      if (!encryptionKeyData) {
        this.encryptionKey = await cryptoUtils.generateEncryptionKey();
        const keyData = await cryptoUtils.exportKey(this.encryptionKey);
        sessionStorage.setItem(this.ENCRYPTION_KEY, JSON.stringify(keyData));
      } else {
        const keyData = JSON.parse(encryptionKeyData);
        this.encryptionKey = await cryptoUtils.importKey(keyData);
      }
      
      console.log('🔒 Enhanced token encryption initialized');
    } catch (error) {
      console.error('Failed to initialize encryption:', error);
      throw error;
    }
  }

  /**
   * Setup background token monitoring
   */
  setupBackgroundMonitoring() {
    // Check token health every 2 minutes
    setInterval(() => {
      this.performHealthCheck();
    }, 2 * 60 * 1000);
    
    // Setup visibility change listener for proactive refresh
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.performHealthCheck();
      }
    });
  }

  /**
   * Perform health check on all stored tokens
   */
  async performHealthCheck() {
    try {
      const keys = Object.keys(sessionStorage);
      const tokenKeys = keys.filter(key => key.startsWith(this.TOKEN_KEY));
      
      for (const key of tokenKeys) {
        const userId = key.replace(`${this.TOKEN_KEY}_`, '');
        const health = await this.getTokenHealth(userId);
        
        if (health.needsRefresh && !this.refreshPromises.has(userId)) {
          console.log(`🔄 Proactive refresh triggered for user ${userId}`);
          this.requestTokenRefresh(userId);
        }
      }
    } catch (error) {
      console.warn('Health check failed:', error);
    }
  }

  /**
   * Store token securely with encryption and metadata
   */
  async storeToken(userId, tokenData, expiresIn = null) {
    try {
      if (!this.encryptionKey) {
        await this.initializeEncryption();
      }

      const expiryTime = expiresIn ? 
        Date.now() + (expiresIn * 1000) : 
        Date.now() + this.TOKEN_EXPIRY;

      const tokenInfo = {
        accessToken: tokenData.accessToken || tokenData,
        refreshToken: tokenData.refreshToken,
        clientId: tokenData.clientId,
        expiresAt: expiryTime,
        createdAt: Date.now(),
        userId: userId,
        scope: tokenData.scope || 'contacts.readonly',
        tokenType: tokenData.token_type || 'Bearer'
      };

      const { encrypted, iv } = await cryptoUtils.encrypt(
        JSON.stringify(tokenInfo), 
        this.encryptionKey
      );

      const storageData = {
        data: cryptoUtils.arrayBufferToBase64(encrypted),
        iv: cryptoUtils.arrayBufferToBase64(iv.buffer),
        timestamp: Date.now(),
        version: '2.0'
      };
      
      // Store in sessionStorage instead of localStorage for better security
      sessionStorage.setItem(`${this.TOKEN_KEY}_${userId}`, JSON.stringify(storageData));
      
      // Schedule proactive refresh
      this.scheduleTokenRefresh(userId, expiryTime);
      
      console.log(`✅ Token stored securely for user ${userId}`);
      return true;
    } catch (error) {
      console.error('Token storage failed:', error);
      return false;
    }
  }

  /**
   * Retrieve and decrypt token with validation
   */
  async getToken(userId) {
    try {
      if (!this.encryptionKey) {
        await this.initializeEncryption();
      }

      const storageData = sessionStorage.getItem(`${this.TOKEN_KEY}_${userId}`);
      
      if (!storageData) {
        return null;
      }

      const { data, iv } = JSON.parse(storageData);
      const encryptedBuffer = cryptoUtils.base64ToArrayBuffer(data);
      const ivBuffer = new Uint8Array(cryptoUtils.base64ToArrayBuffer(iv));

      const decryptedString = await cryptoUtils.decrypt(
        encryptedBuffer, 
        ivBuffer, 
        this.encryptionKey
      );

      const tokenInfo = JSON.parse(decryptedString);

      // Validate token expiry
      if (Date.now() >= tokenInfo.expiresAt) {
        console.log('🚫 Token expired, removing...');
        this.removeToken(userId);
        return null;
      }

      // Check if proactive refresh is needed
      const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
      if (timeUntilExpiry <= this.REFRESH_THRESHOLD && !this.refreshPromises.has(userId)) {
        console.log(`🔄 Token needs refresh (${Math.round(timeUntilExpiry / 60000)} minutes left)`);
        // Start refresh but don't wait for it
        this.requestTokenRefresh(userId, tokenInfo);
      }

      return tokenInfo;
    } catch (error) {
      console.error('Token retrieval failed:', error);
      this.removeToken(userId);
      return null;
    }
  }

  /**
   * Remove token and cleanup
   */
  removeToken(userId) {
    sessionStorage.removeItem(`${this.TOKEN_KEY}_${userId}`);
    this.cancelTokenRefresh(userId);
    this.refreshPromises.delete(userId);
    console.log(`🗑️ Token removed for user ${userId}`);
  }

  /**
   * Schedule automatic token refresh
   */
  scheduleTokenRefresh(userId, expiresAt) {
    // Clear existing timeout
    this.cancelTokenRefresh(userId);
    
    // Calculate refresh time (threshold + buffer before expiry)
    const refreshTime = expiresAt - this.REFRESH_THRESHOLD - this.PROACTIVE_REFRESH_BUFFER - Date.now();
    
    if (refreshTime > 0) {
      const timeoutId = setTimeout(() => {
        console.log(`⏰ Scheduled refresh triggered for user ${userId}`);
        this.requestTokenRefresh(userId);
      }, refreshTime);
      
      this.refreshTimeouts.set(userId, timeoutId);
      console.log(`⏲️ Token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
    }
  }

  /**
   * Cancel scheduled token refresh
   */
  cancelTokenRefresh(userId) {
    if (this.refreshTimeouts.has(userId)) {
      clearTimeout(this.refreshTimeouts.get(userId));
      this.refreshTimeouts.delete(userId);
    }
  }

  /**
   * Request token refresh with retry logic and deduplication
   */
  async requestTokenRefresh(userId, currentToken = null) {
    // Prevent duplicate refresh requests
    if (this.refreshPromises.has(userId)) {
      console.log('🔄 Refresh already in progress, waiting...');
      return this.refreshPromises.get(userId);
    }

    const startTime = Date.now();
    this.metrics.refreshAttempts++;

    const refreshPromise = this.executeTokenRefresh(userId, currentToken);
    this.refreshPromises.set(userId, refreshPromise);

    try {
      const result = await refreshPromise;
      
      this.metrics.refreshSuccesses++;
      this.metrics.totalRefreshTime += Date.now() - startTime;
      this.metrics.averageRefreshTime = this.metrics.totalRefreshTime / this.metrics.refreshSuccesses;
      
      // Notify listeners about successful refresh
      this.notifyRefreshCallbacks('success', userId, result);
      
      return result;
    } catch (error) {
      this.metrics.refreshFailures++;
      this.notifyRefreshCallbacks('error', userId, error);
      throw error;
    } finally {
      this.refreshPromises.delete(userId);
    }
  }

  /**
   * Execute token refresh with exponential backoff
   */
  async executeTokenRefresh(userId, currentToken = null, retryCount = 0) {
    try {
      // Get current token if not provided
      if (!currentToken) {
        currentToken = await this.getToken(userId);
      }

      if (!currentToken || !currentToken.refreshToken) {
        throw new Error('No refresh token available for silent refresh');
      }

      console.log(`🔄 Attempting token refresh (attempt ${retryCount + 1})`);

      // Use Google's OAuth2 endpoint to refresh the token
      const refreshResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: currentToken.clientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
          refresh_token: currentToken.refreshToken,
          grant_type: 'refresh_token'
        })
      });

      if (!refreshResponse.ok) {
        const errorText = await refreshResponse.text();
        throw new Error(`OAuth refresh failed: ${refreshResponse.status} ${errorText}`);
      }

      const refreshedTokens = await refreshResponse.json();
      console.log('✅ OAuth token refresh successful');

      // Create updated token data
      const updatedTokenData = {
        accessToken: refreshedTokens.access_token,
        refreshToken: refreshedTokens.refresh_token || currentToken.refreshToken,
        clientId: currentToken.clientId,
        scope: currentToken.scope,
        token_type: refreshedTokens.token_type || 'Bearer'
      };

      // Store the new token
      await this.storeToken(userId, updatedTokenData, refreshedTokens.expires_in || 3600);

      console.log('✅ Enhanced token refresh completed successfully');
      return refreshedTokens.access_token;

    } catch (error) {
      console.error(`❌ Token refresh attempt ${retryCount + 1} failed:`, error);

      // Implement exponential backoff retry
      if (retryCount < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, retryCount),
          this.retryConfig.maxDelay
        );

        console.log(`⏳ Retrying token refresh in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return this.executeTokenRefresh(userId, currentToken, retryCount + 1);
      }

      // If all retries failed and it's an auth error, clean up
      if (error.message?.includes('invalid_grant') || 
          error.message?.includes('refresh_token') ||
          error.message?.includes('400')) {
        console.log('🔐 Refresh token invalid, removing stored token');
        this.removeToken(userId);
      }

      throw error;
    }
  }

  /**
   * Register callback for token refresh events
   */
  onTokenRefresh(callback) {
    this.refreshCallbacks.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.refreshCallbacks.delete(callback);
    };
  }

  /**
   * Notify refresh callbacks
   */
  notifyRefreshCallbacks(type, userId, data) {
    this.refreshCallbacks.forEach(callback => {
      try {
        callback(type, userId, data);
      } catch (error) {
        console.error('Refresh callback error:', error);
      }
    });
  }

  /**
   * Get token health status
   */
  async getTokenHealth(userId) {
    const token = await this.getToken(userId);
    
    if (!token) {
      return { 
        status: 'missing', 
        needsRefresh: true,
        hasRefreshToken: false
      };
    }

    const timeLeft = token.expiresAt - Date.now();
    const needsRefresh = timeLeft <= this.REFRESH_THRESHOLD;
    
    return {
      status: 'valid',
      expiresIn: timeLeft,
      expiresAt: new Date(token.expiresAt),
      needsRefresh,
      hasRefreshToken: !!token.refreshToken,
      createdAt: new Date(token.createdAt),
      scope: token.scope
    };
  }

  /**
   * Clear all tokens (logout)
   */
  clearAllTokens() {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(this.TOKEN_KEY) || key.startsWith('refresh_timeout_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Clear timeouts and promises
    this.refreshTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.refreshTimeouts.clear();
    this.refreshPromises.clear();
    
    console.log('🧹 All contact tokens cleared');
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Validate token format
   */
  validateTokenFormat(token) {
    if (!token || typeof token !== 'string') {
      return false;
    }

    // Basic OAuth token validation
    return token.length > 10 && /^[A-Za-z0-9+/=_.-]+$/.test(token);
  }
}

// Export singleton instance
export const enhancedSecureTokenManager = new EnhancedSecureTokenManager();
export default enhancedSecureTokenManager;